import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Paper,
  Divider,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DevicesIcon from "@mui/icons-material/Devices";
import PersonIcon from "@mui/icons-material/Person";
import StorageIcon from "@mui/icons-material/Storage";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import { formatPhoneNumber } from "../../Core/util/formatPhone";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";

export default function CompanyDetail({ data, updatedetails }: any) {
  const theme = useTheme();
  const [companyData, setCompanyData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState("");
  const [refreshComp, setRefreshComp] = useState(0);
  const [userBackup, setUserBackup] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [actionType, setActionType] = useState("");

  const handleCloseDeleteModal = () => setDeleteModal(false);

  const deleteAction = () => {
    if (actionType === "deleteCompany") {
      deleteCompany();
    } else if (actionType === "removeUser") {
      removeUser();
    } else if (actionType === "removeDevice") {
      removeDevice();
    }
    handleCloseDeleteModal();
  };

  const openDeleteCompanyModal = () => {
    setActionType("deleteCompany");
    setDeleteModal(true);
  };

  const openRemoveUserModal = (userId: any) => {
    setActionType("removeUser");
    setDeleteModal(true);
    setId(userId);
  };

  const openRemoveDeviceModal = (deviceId: any) => {
    setActionType("removeDevice");
    setDeleteModal(true);
    setId(deviceId);
  };

  const handleGetCompanyData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${ipconfig}/companyinfobyid?companyId=${data?._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setCompanyData(response?.data?.data);
      }
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error getting company details", error);
      setIsLoading(false);
    }
  };

  const deleteCompany = async () => {
    try {
      const response = await axios.delete(
        `${ipconfig}/company/delete?companyId=${companyData?._id}&userBackup=${userBackup}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setRefreshComp(prev => prev + 1);
        if (updatedetails) updatedetails();
      }
    } catch (error: any) {
      console.error("Error deleting company", error);
    }
  };

  const removeDevice = async () => {
    try {
      const response = await axios.put(
        `${ipconfig}/company/removedevice?companyId=${companyData?._id}&removeDeviceId=${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setRefreshComp(prev => prev + 1);
        if (updatedetails) updatedetails();
      }
    } catch (error: any) {
      console.error("Error removing device", error);
    }
  };

  const removeUser = async () => {
    try {
      const response = await axios.put(
        `${ipconfig}/company/removeuser?companyId=${companyData?._id}&removeUserId=${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setRefreshComp(prev => prev + 1);
        if (updatedetails) updatedetails();
      }
    } catch (error: any) {
      console.error("Error removing user", error);
    }
  };

  useEffect(() => {
    if (data?._id) {
      handleGetCompanyData();
    }
  }, [data, refreshComp]);

  if (isLoading || !companyData?.companyName) {
    return <Spinner height="100%" />;
  }

  const generalInfo = [
    { label: "Company Identifier", value: companyData.companyName, icon: <StorageIcon sx={{ fontSize: 18 }} />, isCopyable: true },
    { label: "Database Name", value: companyData.dbName || "N/A", icon: <StorageIcon sx={{ fontSize: 18 }} />, isCopyable: true },
    { label: "Synced By User Email", value: companyData.syncedByEmail || "N/A", icon: <EmailIcon sx={{ fontSize: 18 }} />, isCopyable: true },
    { label: "Last Synced Time", value: companyData.lastSyncedTime ? moment(companyData.lastSyncedTime).format("DD-MM-YYYY hh:mm A") : "N/A", icon: <CalendarTodayIcon sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Header Panel */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
              {companyData?.tally_companyInfo?.Name || "Unnamed Company"}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(companyData?.tally_companyInfo?.Name || "Unnamed Company");
                toast.success("Company Name copied!", { position: "top-center", autoClose: 1000 });
              }}
              sx={{ p: "2px", color: "text.secondary" }}
              title="Copy Company Name"
            >
              <ContentCopyIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Company ID: {companyData._id}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(companyData._id);
                toast.success("Company ID copied!", { position: "top-center", autoClose: 1000 });
              }}
              sx={{ p: "2px", color: "text.secondary" }}
              title="Copy Company ID"
            >
              <ContentCopyIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <Chip
            icon={companyData.syncData ? <CheckCircleIcon /> : <CancelIcon />}
            label={`Sync Data: ${companyData.syncData ? "On" : "Off"}`}
            color={companyData.syncData ? "success" : "default"}
            variant="outlined"
            sx={{ fontWeight: 650, borderRadius: "8px" }}
          />
          <Chip
            icon={companyData.autoImportVoucher ? <CheckCircleIcon /> : <CancelIcon />}
            label={`Import Voucher: ${companyData.autoImportVoucher ? "On" : "Off"}`}
            color={companyData.autoImportVoucher ? "success" : "default"}
            variant="outlined"
            sx={{ fontWeight: 650, borderRadius: "8px" }}
          />
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={openDeleteCompanyModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Delete Company
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* General Specs */}
      <Grid container spacing={3}>
        {generalInfo.map((info, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Box
              sx={{
                p: "16px",
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b50",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                height: "100%"
              }}
            >
              <Box sx={{ color: "primary.main", display: "flex" }}>{info.icon}</Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                  {info.label}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexGrow: 1 }}>
                    {info.value}
                  </Typography>
                  {info.isCopyable && info.value && info.value !== "N/A" && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(info.value);
                        toast.success(`${info.label} copied!`, { position: "top-center", autoClose: 1000 });
                      }}
                      sx={{ p: "2px", color: "text.secondary", flexShrink: 0 }}
                      title={`Copy ${info.label}`}
                    >
                      <ContentCopyIcon sx={{ fontSize: "14px" }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Devices and Users Panels */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Device List Column */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: "20px", borderRadius: "12px", height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "text.primary", display: "flex", alignItems: "center", gap: "8px" }}>
              <DevicesIcon color="primary" />
              Devices List ({companyData?.devices?.length || 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ flexGrow: 1, overflowY: "auto", maxOffsetHeight: "350px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {companyData?.devices?.length > 0 ? (
                companyData.devices.map((device: any, idx: any) => (
                  <ListItem
                    key={idx}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" color="error" onClick={() => openRemoveDeviceModal(device?._id)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px",
                      backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b30",
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>
                        <DevicesIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={device?.deviceName || "Unknown Device"}
                      secondary={
                        <>
                          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                              Device ID: {device?._id || "N/A"}
                            </Typography>
                            {device?._id && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(device._id);
                                  toast.success("Device ID copied!", { position: "top-center", autoClose: 1000 });
                                }}
                                sx={{ p: "1px", color: "text.secondary" }}
                                title="Copy Device ID"
                              >
                                <ContentCopyIcon sx={{ fontSize: "11px" }} />
                              </IconButton>
                            )}
                          </Box>
                          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                              Model: {device?.modelName || "N/A"}
                            </Typography>
                            {device?.modelName && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(device.modelName);
                                  toast.success("Model copied!", { position: "top-center", autoClose: 1000 });
                                }}
                                sx={{ p: "1px", color: "text.secondary" }}
                                title="Copy Model"
                              >
                                <ContentCopyIcon sx={{ fontSize: "11px" }} />
                              </IconButton>
                            )}
                          </Box>
                          <Typography component="span" variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                            Owner: {device?.deviceOwnerEmail || "N/A"}
                          </Typography>
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "14px" }}
                    />
                  </ListItem>
                ))
              ) : (
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                  No devices associated with this company.
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Users List Column */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: "20px", borderRadius: "12px", height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "text.primary", display: "flex", alignItems: "center", gap: "8px" }}>
              <PersonIcon color="primary" />
              Authorized Users ({companyData?.usersInfo?.length || 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ flexGrow: 1, overflowY: "auto", maxOffsetHeight: "350px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {companyData?.usersInfo?.length > 0 ? (
                companyData.usersInfo.map((u: any, idx: any) => {
                  const rootUser = u?.rootDbUserId;
                  return (
                    <ListItem
                      key={idx}
                      secondaryAction={
                        <IconButton edge="end" aria-label="delete" color="error" onClick={() => openRemoveUserModal(u?.companyDbUserId)}>
                          <DeleteIcon />
                        </IconButton>
                      }
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px",
                        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b30",
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "success.light", color: "success.main" }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={rootUser?.name || "Unknown User"}
                        secondary={
                          <>
                            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "4px" }} >
                              <Typography component="span" variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                                user ID: {rootUser?._id || "N/A"}
                              </Typography>
                              {rootUser?._id && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(rootUser?._id);
                                    toast.success("user Id copied!", { position: "top-center", autoClose: 1000 });
                                  }}
                                  sx={{ p: "1px", color: "text.secondary" }}
                                  title="Copy Model"
                                >
                                  <ContentCopyIcon sx={{ fontSize: "11px" }} />
                                </IconButton>
                              )}
                            </Box>
                            <Typography component="span" variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                              Email: {rootUser?.email || "N/A"}
                            </Typography>
                            <Typography component="span" variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                              Phone: {formatPhoneNumber(rootUser?.phoneNumber, rootUser?.countryCode)}
                            </Typography>
                          </>
                        }
                        primaryTypographyProps={{ fontWeight: 600, fontSize: "14px" }}
                      />
                    </ListItem>
                  );
                })
              ) : (
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                  No authorized users found.
                </Box>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={deleteModal}
        onClose={handleCloseDeleteModal}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Removal
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {actionType === "deleteCompany"
              ? "Are you sure you want to delete this company? This action will remove all synchronized settings and links."
              : actionType === "removeUser"
                ? "Are you sure you want to revoke this user's access to this company?"
                : "Are you sure you want to revoke this device's access to this company?"}
          </DialogContentText>

          {actionType === "deleteCompany" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox
                checked={userBackup}
                onChange={(e) => setUserBackup(e.target.checked)}
                id="user-backup-company-checkbox"
              />
              <Typography variant="body2" component="label" htmlFor="user-backup-company-checkbox" sx={{ cursor: "pointer", userSelect: "none" }}>
                Include User Data Backup
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={handleCloseDeleteModal} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={deleteAction} color="error" variant="contained" sx={{ textTransform: "none", fontWeight: 600 }}>
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer style={{ width: "auto" }} />
    </Box>
  );
}
